import * as httpx from "httpx"
import * as uuid from "uuid"
import { kernels } from "brandguard/api"
import { CancellablePromise } from "real-cancellable-promise"
import {
	VideoKernelSettingsCreateRequest,
	VideoKernelSettingsCreateResponse,
	VideoKernelSettingsResponse,
	VideoKernelSearchRequest,
	VideoKernelSearchResponse,
} from "brandguard/brandguard.video_pb"

import { VideoKernelSetting, VideoKernelLogo, ImageClearspace, KernelMetadata } from "brandguard/brandguard.kernel_pb"

export namespace config {
	export function zero(d: Partial<VideoKernelSetting> = {}): VideoKernelSetting {
		return {
			id: "",
			threshold: 100,
			brand_voice: {
				enabled: true,
			},
			sexually_explicit: {
				enabled: true,
			},
			video_quality_general: {
				enabled: false,
			},
			video_violence: {
				enabled: false,
			},
			video_logo_detection: {
				enabled: false,
				logos: [],
			},
			video_font_detection: {
				enabled: false,
				fonts: [],
			},
			video_background_colors: {
				enabled: false,
				colors: [],
			},
			video_lookandfeel: {
				enabled: false,
				rules: [],
			},
			brand_consistency: {
				enabled: true,
			},
			product_detection: {
				enabled: false,
			},
			...d,
		}
	}

	export function latest(
		bid: string,
		id: string,
		...options: httpx.option[]
	): CancellablePromise<VideoKernelSettingsResponse> {
		return httpx.get(
			`${httpx.urlstorage.host()}/brands/${bid}/bg/video/kernel/${id}/settings/${uuid.NIL}`,
			{},
			...options,
		)
	}
}

export namespace searches {
	export function request(d: Partial<VideoKernelSearchRequest> = {}): VideoKernelSearchRequest {
		return {
			offset: 0n,
			brand_id: "",
			limit: 100n,
			...d,
		}
	}

	export function response(d: Partial<VideoKernelSearchResponse> = {}): VideoKernelSearchResponse {
		return {
			next: request(),
			items: [],
			...d,
		}
	}
}

export function search(
	bid: string,
	req: VideoKernelSearchRequest,
	...options: httpx.option[]
): CancellablePromise<VideoKernelSearchResponse> {
	return httpx
		.get<VideoKernelSearchResponse>(`${httpx.urlstorage.host()}/brands/${bid}/bg/video/kernel/`, req, ...options)
		.then((r) => ({
			...r,
			items: (r.items || []).map(kernels.metadata.zero),
		}))
}

export function create_version(
	bid: string,
	id: string,
	req: VideoKernelSettingsCreateRequest,
	...options: httpx.option[]
): CancellablePromise<VideoKernelSettingsCreateResponse> {
	return httpx.post(`${httpx.urlstorage.host()}/brands/${bid}/bg/video/kernel/${id}/settings`, req, ...options)
}

export function rename(
	bid: string,
	id: string,
	version: string,
	name: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata> {
	return httpx.patch(
		`${httpx.urlstorage.host()}/brands/${bid}/bg/video/kernel/${id}/name/${version}`,
		{ name },
		...options,
	)
}

export function reactivate(
	bid: string,
	id: string,
	version: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata> {
	return httpx.post(
		`${httpx.urlstorage.host()}/brands/${bid}/bg/video/kernel/${id}/reactivate/${version}`,
		{},
		...options,
	)
}

export function getactive(
	bid: string,
	id: string,
	...options: httpx.option[]
): CancellablePromise<KernelMetadata | null> {
	return httpx
		.get<{ kernel?: KernelMetadata }>(
			`${httpx.urlstorage.host()}/brands/${bid}/bg/video/kernel/${id}/${uuid.NIL}`,
			{},
			...options,
		)
		.then((r) => r?.kernel || null)
		.catch(() => Promise.resolve(null))
}

export type { KernelMetadata }

export namespace logos {
	export function zero(d: Partial<VideoKernelLogo> = {}): VideoKernelLogo {
		return {
			mimetype: "",
			md5: "",
			description: "",
			image: "",
			clearspace: { horizontal: 0, vertical: 0 },
			usage: "",
			...d,
		}
	}
	export namespace clearspace {
		export function zero(d: Partial<ImageClearspace> = {}): ImageClearspace {
			return {
				horizontal: 0,
				vertical: 0,
				...d,
			}
		}
	}
}
